import React, { useEffect, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { components } from '../../components';
import { hooks } from '../../hooks';
import { items } from '../../items';
import RateUs from '../../items/RateUs';
import { Routes } from '../../routes';
import type { CarouselType, DishType, MenuType } from '../../types';
import { ReviewTypes } from '../../types/ReviewType';
import { getItemFromLocalStorage } from '../../utils/getItemFromLocalStorage';

export const Home: React.FC = () => {
  const navigate = hooks.useNavigate();

  const {menuLoading, menu} = hooks.useGetMenu();
  const {dishesLoading, dishes} = hooks.useGetDishes();
  const {reviewsLoading, reviews} = hooks.useGetReviews();
  const {carouselLoading, carousel} = hooks.useGetCarousel();
  const [reviewData, setReviewData ] = useState<ReviewTypes[]>([])
  const loading: boolean =
    menuLoading || dishesLoading || reviewsLoading || carouselLoading;

  const [opacity, setOpacity] = useState<number>(0);

  hooks.useScrollToTop();
  hooks.useOpacity(setOpacity);

  const [activeSlide, setActiveSlide] = useState(0);

  const handleSlideChange = (swiper: any) => {
    setActiveSlide(swiper.activeIndex);
  };
  useEffect(() => {
    if (reviews && reviews.length > 0) {
      setReviewData(reviews[0].reviews);
    }
  }, [reviews]);
  
  

  const renderCarousel = (): JSX.Element => {
    const dish = dishes[2];

    return (
      <section style={{
        marginBottom: 30,
        marginTop: 10,
        position: 'relative',
        borderRadius: '0 0 20px 20px',
        overflow: 'hidden',
      }}>
        <Swiper
          slidesPerView={'auto'}
          pagination={{clickable: true}}
          navigation={true}
          mousewheel={true}
          onSlideChange={handleSlideChange}
          style={{
            borderRadius: '0 0 20px 20px',
          }}
        >
          {carousel.map((banner: CarouselType) => {
            return (
              <SwiperSlide key={banner.id}>
                <img
                  alt={'banner'}
                  src={banner.banner}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                  }}
                  className='clickable'
                  onClick={() => navigate(Routes.Dish, {state: {dish: dish}})}
                />
              </SwiperSlide>
            );
          })}
        </Swiper>

        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'absolute',
            bottom: 20,
            zIndex: 1,
            width: '100%',
            gap: 8,
          }}
        >
          {carousel.map((_, index) => {
            return (
              <div
                key={_.id}
                style={{
                  width: activeSlide === index ? 24 : 8,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor:
                    activeSlide === index
                      ? 'var(--white-color)'
                      : 'rgba(255, 255, 255, 0.5)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: activeSlide === index 
                    ? '0 2px 8px rgba(255, 255, 255, 0.4)' 
                    : 'none',
                }}
              />
            );
          })}
        </div>
      </section>
    );
  };

  const renderMenu = (): JSX.Element => {
    return (
      <section style={styles.categorySection}>
        <components.BlockHeading
          title='What’s on your mind?' // More engaging title
          viewAllOnClick={() => navigate(Routes.MenuList, { state: { menuName: menu[0]?.name } })}
          containerStyle={styles.headingStyle}
        />
        
        <div style={styles.swiperWrapper}>
          <Swiper
            spaceBetween={16} // Increased breathing room
            slidesPerView={'auto'}
            grabCursor={true}
            slidesOffsetBefore={20} // Replaces the manual margin check in the loop
            slidesOffsetAfter={20}
          >
            {menu.map((item: MenuType) => (
              <SwiperSlide key={item.id} style={{ width: 'auto' }}>
                <div 
                  style={styles.categoryItem}
                  onClick={() => navigate(Routes.MenuList, { state: { menuName: item.name } })}
                  className="category-hover-effect"
                  onMouseEnter={(e) => {
                    const circle = e.currentTarget.querySelector('.image-circle') as HTMLElement;
                    if (circle) {
                      circle.style.transform = 'scale(1.08)';
                      circle.style.boxShadow = '0 8px 20px rgba(0, 176, 185, 0.2)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    const circle = e.currentTarget.querySelector('.image-circle') as HTMLElement;
                    if (circle) {
                      circle.style.transform = 'scale(1)';
                      circle.style.boxShadow = '0 4px 15px rgba(0,0,0,0.08)';
                    }
                  }}
                >
                  {/* Image Circle */}
                  <div style={styles.imageCircle} className="image-circle">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        style={styles.circleImg}
                      />
                    ) : (
                      <components.FallBackImage name={item.name} />
                    )}
                  </div>
  
                  {/* Label */}
                  <span style={styles.categoryName}>
                    {item.name}
                  </span>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>
    );
  };
  
  const styles = {
    categorySection: {
      marginBottom: 32,
      marginTop: 8,
    },
    headingStyle: {
      paddingLeft: 20,
      paddingRight: 20,
      marginBottom: 16,
    },
    swiperWrapper: {
      width: '100%',
    },
    categoryItem: {
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer',
      transition: 'transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
    },
    imageCircle: {
      width: 80,
      height: 80,
      borderRadius: '12px',
      padding: '0',
      backgroundColor: '#f8f9fa',
      boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
      overflow: 'hidden',
      border: 'none',
      marginBottom: 4,
      transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    },
    circleImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
      borderRadius: '12px',
    },
    categoryName: {
      fontSize: '13px',
      fontWeight: 600,
      color: '#495057',
      textAlign: 'center' as const,
      fontFamily: 'DM Sans, sans-serif',
      maxWidth: 90,
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      letterSpacing: '-0.2px',
    }
  };

  const renderRecommendedForYou = (): JSX.Element => {
    return (
      <div style={{marginBottom: 40}}>
        <components.BlockHeading
          title='Todays Special'
          containerStyle={{paddingLeft: 20, paddingRight: 20, marginBottom: 16}}
        />
        <div style={{width: '100%'}}>
          <Swiper
            spaceBetween={16}
            slidesPerView={'auto'}
            pagination={{clickable: true}}
            navigation={true}
            mousewheel={true}
            slidesOffsetBefore={20}
            slidesOffsetAfter={20}
          >
            {dishes.filter(f=>f.isTodaySpecial===true).map((dish: DishType, index: number, array: DishType[]) => {
              const isLast = index === array.length - 1;
              return (
                <SwiperSlide
                  key={dish.id}
                  style={{
                    width: '180px',
                    minWidth: '180px',
                    maxWidth: '180px',
                    height: 'auto',
                  }}
                >
                  <items.RecomendedItem
                    index={index}
                    isLast={isLast}
                    dish={dish}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>
    );
  };
  const chefSpecial = (): JSX.Element => {
    return (
      <div style={{marginBottom: 40}}>
        <components.BlockHeading
          title="Chef's Special"
          containerStyle={{paddingLeft: 20, paddingRight: 20, marginBottom: 16}}
        />
        <div style={{width: '100%'}}>
          <Swiper
            spaceBetween={16}
            slidesPerView={'auto'}
            pagination={{clickable: true}}
            navigation={true}
            mousewheel={true}
            slidesOffsetBefore={20}
            slidesOffsetAfter={20}
          >
            {dishes.filter(f=>f.isChefSpecial===true).map((dish: DishType, index: number, array: DishType[]) => {
              const isLast = index === array.length - 1;
              return (
                <SwiperSlide
                  key={dish.id}
                  style={{
                    width: '180px',
                    minWidth: '180px',
                    maxWidth: '180px',
                    height: 'auto',
                  }}
                >
                  <items.RecomendedItem
                    index={index}
                    isLast={isLast}
                    dish={dish}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>
      </div>
    );
  };

  const renderReviews = (): JSX.Element => {
  const MENU_CONFIG=getItemFromLocalStorage("menuConfiguration")
   
    return (
      <section style={{marginBottom: 40, paddingLeft: 20, paddingRight: 20}}>
        <components.BlockHeading
          title="What our customer says"
          viewAllOnClick={() => navigate(Routes.Reviews)}
          containerStyle={{ marginLeft: 0, marginRight: 0, marginBottom: 16 }}
        />
  <RateUs avgRating={reviews[0]?.avgRating}/>
        {MENU_CONFIG.showReviews&&<div className="w-full">
          <Swiper
            spaceBetween={14}
            slidesPerView={'auto'}
            pagination={{ clickable: true }}
            navigation={true}
            mousewheel={true}
          >
            {reviewData.slice(0, 5)?.map((review, index, array) => {
              const isLast = index === array.length - 1;
              return (
                <SwiperSlide key={review.name} className="w-auto">
                  <items.HomeReviewItem
                    index={index}
                    isLast={isLast}
                    review={review}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>}
  
        {/* Rate Us Section */}
       
      </section>
    );
  };
  

  const renderContent = (): JSX.Element => {
    if (loading) return <components.Loader />;

    return (
      <main 
        className='scrollable'
        style={{
          backgroundColor: 'var(--main-background)',
          paddingBottom: 20,
        }}
      >
        {renderCarousel()}
        {renderMenu()}
        {renderRecommendedForYou()}
        {chefSpecial()}
        {renderReviews()}
      </main>
    );
  };

  return (
    <div
      id='screen'
      style={{opacity}}
    >
      {renderContent()}
    </div>
  );
};
